// Controles da lista de descontos: abas de situação, busca, posto, combustível e tipo. Só apresentação:
// quem usa decide o que cada clique faz (DiscountList.tsx).
import type { ChangeEvent } from 'react';
import { Box, ButtonBase, Skeleton } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { visuallyHidden } from '@mui/utils';
import { __t } from '../../../i18n/t';
import { fmtN } from './discountStatus';

const INK = '#1e293b';
const VIOLET = '#4433B4';
const focusSx = { '&.Mui-focusVisible': { outline: `2px solid ${VIOLET}`, outlineOffset: 2 } } as const;

export interface TabItem {
    key: string;
    label: string;
    /** undefined = ainda contando. */
    count: number | undefined;
}

export const StatusTabs = ({ items, active, onPick }: { items: TabItem[]; active: string; onPick: (key: string) => void }) => (
    <Box
        role="group"
        aria-label={__t('Situação dos descontos')}
        // Quebra em linhas no celular em vez de rolar: nenhuma aba fica escondida.
        sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px', p: '4px', borderRadius: '12px', bgcolor: '#f1f5f9', alignSelf: { xs: 'stretch', sm: 'flex-start' }, maxWidth: '100%' }}
    >
        {items.map(item => {
            const on = item.key === active;
            return (
                <ButtonBase
                    key={item.key}
                    aria-pressed={on}
                    onClick={() => onPick(item.key)}
                    sx={{
                        minHeight: 40, px: '14px', borderRadius: '9px', bgcolor: on ? '#fff' : 'transparent', boxShadow: on ? '0 1px 3px rgba(15,23,42,.12)' : 'none',
                        color: INK, fontSize: 14, fontWeight: on ? 700 : 500, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                        whiteSpace: 'nowrap', flex: { xs: '1 1 auto', sm: '0 0 auto' }, ...focusSx,
                    }}
                >
                    {item.label}
                    <Box
                        component="span"
                        sx={{
                            minWidth: 22, height: 20, px: '6px', borderRadius: '999px', bgcolor: on ? VIOLET : '#e2e8f0', color: on ? '#fff' : '#334155',
                            fontSize: 12, fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontVariantNumeric: 'tabular-nums',
                        }}
                    >
                        {item.count === undefined ? <Skeleton width={10} aria-hidden="true" /> : fmtN(item.count)}
                    </Box>
                </ButtonBase>
            );
        })}
    </Box>
);

export const TypeChips = ({ items, active, onPick }: { items: Array<{ key: string; label: string }>; active: string; onPick: (key: string) => void }) => (
    <Box role="group" aria-label={__t('Tipo')} sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
        {items.map(item => {
            const on = item.key === active;
            return (
                <ButtonBase
                    key={item.key}
                    aria-pressed={on}
                    onClick={() => onPick(item.key)}
                    sx={{
                        minHeight: 40, px: '14px', borderRadius: '999px', border: `1px solid ${on ? VIOLET : '#7c8595'}`, bgcolor: on ? VIOLET : '#fff',
                        color: on ? '#fff' : INK, fontSize: 13.5, fontWeight: 500, whiteSpace: 'nowrap', ...focusSx,
                    }}
                >
                    {item.label}
                </ButtonBase>
            );
        })}
    </Box>
);

export const SearchField = ({ value, onChange }: { value: string; onChange: (value: string) => void }) => (
    <Box
        component="label"
        htmlFor="discount-search"
        sx={{
            display: 'flex', alignItems: 'center', gap: 1, flex: '0 1 340px', width: { xs: '100%', sm: 'auto' }, minWidth: { xs: 0, sm: 240 }, height: 44, px: 1.5,
            border: '1px solid #7c8595', borderRadius: '8px', bgcolor: '#fff', cursor: 'text', boxSizing: 'border-box', '&:focus-within': { boxShadow: `0 0 0 2px ${VIOLET}` },
        }}
    >
        <SearchIcon aria-hidden="true" sx={{ fontSize: 20, color: '#64748b', flex: 'none' }} />
        <Box component="span" sx={visuallyHidden}>{__t('Buscar desconto')}</Box>
        <Box
            component="input"
            id="discount-search"
            type="search"
            autoComplete="off"
            value={value}
            onChange={(event: ChangeEvent<HTMLInputElement>) => onChange(event.target.value)}
            placeholder={__t('Buscar por nome, código ou posto')}
            // 16 px no celular: abaixo disso o Safari do iPhone dá zoom na página ao tocar no campo
            sx={{ border: 0, outline: 'none', width: '100%', minWidth: 0, alignSelf: 'stretch', fontSize: { xs: 16, sm: 15 }, bgcolor: 'transparent', color: INK, fontFamily: 'inherit', '&::placeholder': { color: '#64748b', opacity: 1 } }}
        />
    </Box>
);

/** Seletor de filtro (posto, combustível): "Todos" mais um item por opção. */
export const FilterSelect = ({ label, allLabel, items, value, onChange }: {
    label: string; allLabel: string; items: Array<{ id: string; name: string }>; value: string; onChange: (id: string) => void;
}) => (
    // Mesma caixa da busca ao lado. O <select> continua nativo (no celular abre a lista do sistema), mas sem a
    // seta do navegador (appearance: none): a seta é o ícone do MUI, por cima e sem receber clique.
    <Box component="label" sx={{ position: 'relative', display: 'flex', width: { xs: '100%', sm: 'auto' }, flex: '0 1 260px', minWidth: 0 }}>
        <Box component="span" sx={visuallyHidden}>{label}</Box>
        <Box
            component="select"
            value={value}
            onChange={(event: ChangeEvent<HTMLSelectElement>) => onChange(event.target.value)}
            sx={{
                appearance: 'none', WebkitAppearance: 'none', MozAppearance: 'none',
                width: '100%', height: 44, minWidth: 0, pl: 1.5, pr: 5, border: '1px solid #7c8595', borderRadius: '8px', bgcolor: '#fff', color: INK, fontSize: { xs: 16, sm: 15 },
                fontFamily: 'inherit', cursor: 'pointer', boxSizing: 'border-box', textOverflow: 'ellipsis', outline: 'none',
                '&:hover': { borderColor: INK }, '&:focus-visible': { boxShadow: `0 0 0 2px ${VIOLET}` },
            }}
        >
            <option value="">{allLabel}</option>
            {items.map(item => (
                <option key={item.id} value={item.id}>{item.name}</option>
            ))}
        </Box>
        <ExpandMoreIcon aria-hidden="true" sx={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', fontSize: 22, color: '#64748b', pointerEvents: 'none' }} />
    </Box>
);
